import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, Gift, CreditCard, Shield, AlertCircle, Zap, Award, Star, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';

export default function PayFlightsWithGiftCardsCredits() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonicalUrl = 'https://defera.app/guides/pay-flights-with-gift-cards-credits';
  const metaTitle = 'How to Pay for Flights With Airline Gift Cards & Travel Credits (2026)';
  const metaDescription = 'Learn how to use airline gift cards and travel credits for flight bookings. Discover why Defera is the world\'s first BNPL service that accepts airline credits and gift cards.';

  const competitorComparison = [
    {
      provider: 'Affirm',
      acceptsGiftCards: 'No',
      acceptsTravelCredits: 'No',
      notes: 'Lender; doesn\'t redeem airline credits itself'
    },
    {
      provider: 'Klarna',
      acceptsGiftCards: 'No',
      acceptsTravelCredits: 'No',
      notes: 'Pay-in-4 or monthly; no travel credit integration'
    },
    {
      provider: 'Uplift (Flex Pay by Upgrade)',
      acceptsGiftCards: 'Varies',
      acceptsTravelCredits: 'Varies',
      notes: 'Offered by partner airlines; combining with credits depends on the airline'
    },
    {
      provider: 'PayPal Pay in 4',
      acceptsGiftCards: 'No',
      acceptsTravelCredits: 'No',
      notes: 'General BNPL; not travel-specific'
    },
    {
      provider: 'Defera',
      acceptsGiftCards: 'Yes',
      acceptsTravelCredits: 'Yes',
      notes: 'World\'s first BNPL to accept airline credits & gift cards'
    }
  ];

  const giftCardTips = [
    {
      title: 'Check the balance and the rules',
      detail: 'Rules differ by airline. As of November 2026, Delta and Southwest gift cards don\'t expire, while JetBlue no longer sells new gift cards and redeems existing ones by phone. Verify before booking.'
    },
    {
      title: 'Combine with installments',
      detail: 'Use your gift card to reduce the total, then pay the rest over time with a payment plan.'
    },
    {
      title: 'Know the airline\'s policy',
      detail: 'Some gift cards can\'t pay for extras: Delta\'s, for example, can\'t cover bag fees or upgrades. Read the fine print.'
    },
    {
      title: 'Keep confirmation records',
      detail: 'Save gift card numbers and transaction confirmations until your trip is complete.'
    }
  ];

  const travelCreditTips = [
    {
      title: 'Track expiration dates',
      detail: 'Many airline credits expire about a year after they\'re issued (United\'s, for example, as of November 2026). Set calendar reminders.'
    },
    {
      title: 'Understand transfer rules',
      detail: 'Many credits stay with the original traveler: United\'s future flight credits generally do, and Delta needs the original ticket holder\'s name to rebook with an eCredit. Some, like United travel certificates, can be used by anyone.'
    },
    {
      title: 'Check fare class restrictions',
      detail: 'Some credits only apply to certain fare classes or routes.'
    },
    {
      title: 'Combine strategically',
      detail: 'Apply credits first, then use Defera installments for the remaining balance.'
    }
  ];

  const faqs = [
    {
      question: 'Can I use airline gift cards with BNPL services?',
      answer: 'Affirm and Klarna don\'t redeem airline gift cards themselves, and with Uplift (now Flex Pay by Upgrade) it depends on the airline\'s checkout. Defera is the world\'s first buy now pay later travel service that accepts airline gift cards, allowing you to reduce your total and pay the rest over time.'
    },
    {
      question: 'What are airline travel credits?',
      answer: 'Travel credits are issued by airlines when you cancel a flight or receive compensation. They can be applied to future bookings before they expire, often about a year after they\'re issued; rules vary by airline.'
    },
    {
      question: 'Why don\'t other BNPL services accept travel credits?',
      answer: 'General-purpose BNPL providers like Affirm and Klarna pay the merchant at checkout and don\'t connect to airline credit systems; plans offered by an airline may let you combine its credits, depending on that airline. Defera was built specifically for travel, enabling credit and gift card acceptance.'
    },
    {
      question: 'How do I apply gift cards or credits with Defera?',
      answer: 'On the checkout page, add an airline travel credit or eCredit, a certificate or voucher, or an airline gift card under Travel Credits & Gift Cards. The amount comes off your fare right away and shows as Pending verification until our team confirms it with the airline. Prepaid cards, including general-purpose gift cards, can\'t be used to make plan payments.'
    },
    {
      question: 'Can I combine gift cards, credits, and installments?',
      answer: 'Yes. With Defera, you can stack airline gift cards and travel credits to reduce your total, then pay the remainder in weekly or bi-weekly payments with no credit check and no interest.'
    }
  ];

  return (
    <>
      <PageSeo
        path="/guides/pay-flights-with-gift-cards-credits"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: metaTitle,
            description: metaDescription,
            author: { '@type': 'Organization', name: 'Defera' },
            publisher: {
              '@type': 'Organization',
              name: 'Defera',
              logo: { '@type': 'ImageObject', url: 'https://defera.app/defera-transparent.png' }
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer }
            }))
          },
        ]}
      />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero */}
        <section className="pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-white/45 text-xs font-normal uppercase tracking-wider">
                  Defera Exclusive
                </span>
                <span className="text-white/40 text-xs">Updated November 2026</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                How to Pay for Flights With Airline Gift Cards & Travel Credits
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                Got airline gift cards or travel credits sitting unused? General BNPL providers like <strong className="text-white">Affirm</strong> and <strong className="text-white">Klarna</strong> don’t redeem them, and with <strong className="text-white">Uplift</strong> (now Flex Pay by Upgrade) it depends on the airline. <strong className="text-white">Defera is the world's first buy now pay later travel service that accepts airline gift cards and travel credits</strong>—so you can reduce your total and pay the rest over time.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Quick Answer */}
        <section className="pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#1d1d1f] border border-white/[0.08] rounded-[1.25rem] p-6 md:p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-6 h-6 text-white/90" />
                <h2 className="text-xl md:text-2xl font-bold text-white">Why Defera is different</h2>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    <strong className="text-white">World's first BNPL</strong> that accepts airline gift cards and travel credits.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    <strong className="text-white">Reduce your total</strong> with credits, then pay the remainder in installments.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    <strong className="text-white">No credit check, no interest</strong>—transparent payment plans.
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Competitor Comparison Table */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">BNPL Providers: Gift Card & Credit Acceptance</h2>
              <p className="text-white/80 mb-6">
                Most buy now pay later services were built for general retail—not travel—so they don’t redeem airline credits themselves. Provider terms are as of November 2026.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-white font-semibold">Provider</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Gift Cards</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Travel Credits</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitorComparison.map((row, idx) => (
                      <tr key={idx} className={`border-b border-white/10 ${row.provider === 'Defera' ? 'bg-white/[0.04]' : ''}`}>
                        <td className={`py-3 px-4 font-medium ${row.provider === 'Defera' ? 'text-white/55' : 'text-white/90'}`}>
                          {row.provider}
                          {row.provider === 'Defera' && <Star className="w-4 h-4 inline ml-2 text-white/90 fill-brand-400" />}
                        </td>
                        <td className="py-3 px-4">
                          <span className={row.acceptsGiftCards === 'Yes' ? 'text-white/55 font-medium' : row.acceptsGiftCards === 'No' ? 'text-red-400' : 'text-white/80'}>
                            {row.acceptsGiftCards}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={row.acceptsTravelCredits === 'Yes' ? 'text-white/55 font-medium' : row.acceptsTravelCredits === 'No' ? 'text-red-400' : 'text-white/80'}>
                            {row.acceptsTravelCredits}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-white/80">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Gift Card Tips */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-6">
                <Gift className="w-7 h-7 text-white/70" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">Using Airline Gift Cards</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {giftCardTips.map((tip, index) => (
                  <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-white/[0.08] text-white/90 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">{tip.title}</h3>
                        <p className="text-white/80 text-sm leading-relaxed">{tip.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Travel Credit Tips */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-7 h-7 text-white/55" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">Using Airline Travel Credits</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {travelCreditTips.map((tip, index) => (
                  <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-white/55 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">{tip.title}</h3>
                        <p className="text-white/80 text-sm leading-relaxed">{tip.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* How Defera Works */}
        <section className="py-14 px-6 bg-zinc-950/50 border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6 text-white/55" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">How Defera handles gift cards & credits</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-white/55 font-bold text-lg">1</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Search & select</h3>
                  <p className="text-white/80 text-sm">Find your flight on Defera.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-white/55 font-bold text-lg">2</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Apply credits</h3>
                  <p className="text-white/80 text-sm">Add an airline travel credit, voucher or airline gift card under Travel Credits &amp; Gift Cards at checkout. It comes off your fare right away and shows “Pending verification” until our team confirms it with the airline.</p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <span className="text-white/55 font-bold text-lg">3</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Pay the rest</h3>
                  <p className="text-white/80 text-sm">Pay the rest with a deposit, then weekly or bi-weekly payments. No credit check, no interest.</p>
                </div>
              </div>

              <p className="text-white/80 text-sm mb-6">
                <strong className="text-white">Good to know:</strong> only airline gift cards go in Travel Credits &amp; Gift Cards. Your plan payments need a debit or credit card; prepaid cards, including general-purpose gift cards like Visa or Mastercard gift cards, aren’t accepted on payment plans.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/search')} className="bg-white hover:bg-white/90 text-black px-6 py-3 rounded-full font-semibold text-sm">
                  Search Flights
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/guides/airlines-that-offer-buy-now-pay-later')}
                  className="border border-white/15 text-white hover:bg-white/10 px-6 py-3 rounded-full font-semibold text-sm"
                >
                  See which sites offer pay later
                  <Plane className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why Competitors Don't Support This */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Why Affirm &amp; Klarna Don’t Accept Airline Credits</h2>
              <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-2xl p-6">
                <p className="text-white/80 leading-relaxed mb-4">
                  General BNPL providers like <strong className="text-white">Affirm</strong> and <strong className="text-white">Klarna</strong> were designed for retail purchases. They pay the merchant at the point of sale and don’t redeem airline travel credits or gift cards themselves. <strong className="text-white">Uplift</strong>, now called Flex Pay by Upgrade, is offered by partner airlines at their own checkout, so whether you can combine it with that airline’s credits or gift cards depends on the airline.
                </p>
                <p className="text-white/80 leading-relaxed mb-4">
                  <strong className="text-white">Defera was built from the ground up for travel.</strong> That means native support for airline gift cards, travel credits, and the unique requirements of flight booking—including price protection, flexible schedules, and no credit checks.
                </p>
                <div className="mt-4 bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-white/90 flex-shrink-0 mt-0.5" />
                  <p className="text-white/80 text-sm">
                    <strong className="text-white/90">Pro tip:</strong> If you have expiring travel credits, don't let them go to waste. Apply them with Defera and pay the difference over time.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl px-6 py-5 hover:border-white/20 transition-colors">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Related guides</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/guides/pay-for-flights-with-debit-card" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  How to pay for flights with a debit card
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-installments-bnpl-payment-options" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Book flights on installments without a credit card
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/defera-vs-affirm-klarna-uplift" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Defera vs Affirm, Klarna & Uplift
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-layaway-vs-bnpl" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight layaway vs BNPL: what’s the difference?
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
